import { useState } from 'react';
import { useAppStore } from '@/store/store';
import { selectFreightCost, selectShipmentSummary, selectFCLCost } from '@/store/selectors';
import { Accessorial, FreightMode, WeightBasis } from '@freight-cbm/shared-types';
import { ExpressionInput } from 'calc-controls';
import { CurrencySelect } from '@/components/shared/CurrencySelect';
import { Tooltip } from '@/components/shared/Tooltip';
import { X } from 'lucide-react';

export function CostPanel() {
  const mode = useAppStore((s) => s.mode);
  const costInput = useAppStore((s) => s.costInput);
  const setRatePerKg = useAppStore((s) => s.setRatePerKg);
  const setRatePerCBM = useAppStore((s) => s.setRatePerCBM);
  const setRatePerRevenueTon = useAppStore((s) => s.setRatePerRevenueTon);
  const setRatePerContainer = useAppStore((s) => s.setRatePerContainer);
  const setCurrency = useAppStore((s) => s.setCurrency);
  const setAccessorials = useAppStore((s) => s.setAccessorials);

  const freightCost = useAppStore(selectFreightCost);
  const summary = useAppStore(selectShipmentSummary);
  const fclCost = useAppStore(selectFCLCost);
  const hasRate = costInput.ratePerKg > 0 || costInput.ratePerCBM > 0 || costInput.ratePerRevenueTon > 0;
  const cost = hasRate ? freightCost : null;

  const [accessorialName, setAccessorialName] = useState('');
  const [accessorialCost, setAccessorialCost] = useState(0);

  const addAccessorial = () => {
    const name = accessorialName.trim();
    if (!name || isNaN(accessorialCost) || accessorialCost <= 0) return;
    const newAccess: Accessorial = { name, cost: accessorialCost };
    setAccessorials([...costInput.accessorials, newAccess]);
    setAccessorialName('');
    setAccessorialCost(0);
  };

  const removeAccessorial = (idx: number) => {
    setAccessorials(costInput.accessorials.filter((_, i) => i !== idx));
  };

  return (
    <div className="card" data-testid="cost-panel">
      <h3 className="section-title">Freight Cost Estimation</h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        {(mode === FreightMode.Air || mode === FreightMode.Courier) && (
          <ExpressionInput
            label="Rate per kg"
            value={costInput.ratePerKg}
            onChange={setRatePerKg}
            unit={costInput.currency}
            min={0}
            decimals={2}
          />
        )}
        {mode === 'ocean-lcl' && (
          <>
            <ExpressionInput
              label="Rate per CBM"
              value={costInput.ratePerCBM}
              onChange={setRatePerCBM}
              unit={costInput.currency}
              min={0}
              decimals={2}
            />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-1)' }}>
              <ExpressionInput
                label="Rate per Revenue Ton"
                value={costInput.ratePerRevenueTon}
                onChange={setRatePerRevenueTon}
                unit={costInput.currency}
                min={0}
                decimals={2}
              />
              <Tooltip content="If set, overrides rate per CBM. Billed per revenue ton (W/M) — the greater of volume or weight.">
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', cursor: 'help', lineHeight: '2.5', whiteSpace: 'nowrap' }}>?</span>
              </Tooltip>
            </div>
          </>
        )}
        <ExpressionInput
          label="Rate per container (FCL)"
          value={costInput.ratePerContainer}
          onChange={setRatePerContainer}
          unit={costInput.currency}
          min={0}
          decimals={2}
        />
        <CurrencySelect label="Currency" value={costInput.currency} onChange={setCurrency} />
      </div>

      <div style={{ marginBottom: 'var(--space-3)' }}>
        <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>Accessorials</h4>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', alignItems: 'center' }}>
          <input value={accessorialName} onChange={(e) => setAccessorialName(e.target.value)} placeholder="Name" aria-label="Accessorial name" style={{ flex: 2, padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }} />
          <ExpressionInput
            value={accessorialCost}
            onChange={setAccessorialCost}
            unit={costInput.currency}
            min={0}
            decimals={2}
          />
          <button type="button" className="btn btn-sm" onClick={addAccessorial} aria-label="Add accessorial charge">Add</button>
        </div>
        {costInput.accessorials.map((a, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-1) 0', fontSize: 'var(--font-size-sm)', gap: 'var(--space-2)' }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}: {costInput.currency} {a.cost.toFixed(2)}</span>
            <button type="button" className="btn btn-sm" onClick={() => removeAccessorial(i)} aria-label="Remove accessorial"><X size={14} /></button>
          </div>
        ))}
      </div>

      {cost && (
        <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)' }}>Base Cost ({cost.chargeableUnit})</span>
            <span style={{ fontFamily: 'var(--font-family-mono)' }}>{cost.currency} {cost.baseCost.toFixed(2)}</span>
          </div>
          {cost.accessorialCost > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--font-size-sm)' }}>Accessorials</span>
              <span style={{ fontFamily: 'var(--font-family-mono)' }}>{cost.currency} {cost.accessorialCost.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-md)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-2)' }}>
            <span>Total</span>
            <span style={{ fontFamily: 'var(--font-family-mono)' }}>{cost.currency} {cost.totalCost.toFixed(2)}</span>
          </div>
          {summary.totalPackages > 0 && (
            <div style={{ marginTop: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-border)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
                <span>Cost / carton</span>
                <span style={{ fontFamily: 'var(--font-family-mono)' }}>{cost.currency} {(cost.totalCost / summary.totalPackages).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
                <span>Cost / CBM</span>
                <span style={{ fontFamily: 'var(--font-family-mono)' }}>{summary.totalCBM > 0 ? `${cost.currency} ${(cost.totalCost / summary.totalCBM).toFixed(2)}` : '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Cost / kg</span>
                <span style={{ fontFamily: 'var(--font-family-mono)' }}>{summary.grossWeightKg > 0 ? `${cost.currency} ${(cost.totalCost / summary.grossWeightKg).toFixed(2)}` : '—'}</span>
              </div>
            </div>
          )}
          {summary.weightBasis === WeightBasis.Dimensional && (
            <div style={{ marginTop: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-border)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              {(() => {
                const dimPenaltyKg = summary.chargeableWeightKg - summary.grossWeightKg;
                const isOcean = mode === FreightMode.OceanLCL;
                const dimPenaltyCost = isOcean
                  ? (dimPenaltyKg / 1000) * cost.rateUsed
                  : dimPenaltyKg * cost.rateUsed;
                const effectiveRatePerKg =
                  summary.chargeableWeightKg > 0 ? cost.totalCost / summary.chargeableWeightKg : 0;
                return (
                  <>
                    <div style={{ marginBottom: 'var(--space-1)' }}>
                      DIM weight billed: {dimPenaltyKg.toFixed(2)} kg above actual — costing {cost.currency} {dimPenaltyCost.toFixed(2)}
                    </div>
                    <div>
                      Effective rate: {effectiveRatePerKg.toFixed(2)} / kg (nominal {cost.rateUsed.toFixed(2)} / kg)
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {fclCost && (
        <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--color-border)' }}>
          <div style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>
            FCL Cost Estimate
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)' }}>Base Cost ({fclCost.chargeableAmount} × {fclCost.rateUsed.toFixed(2)})</span>
            <span style={{ fontFamily: 'var(--font-family-mono)' }}>{fclCost.currency} {fclCost.baseCost.toFixed(2)}</span>
          </div>
          {fclCost.accessorialCost > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--font-size-sm)' }}>Accessorials</span>
              <span style={{ fontFamily: 'var(--font-family-mono)' }}>{fclCost.currency} {fclCost.accessorialCost.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-md)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-2)' }}>
            <span>FCL Total ({fclCost.chargeableUnit})</span>
            <span style={{ fontFamily: 'var(--font-family-mono)' }}>{fclCost.currency} {fclCost.totalCost.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
